"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Button, ButtonProps, buttonVariants } from "@/components/ui/button";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import Link from "next/link";
import { useTranslation } from "@/app/i18n/client";
import { ArrowsTripleLeft, ArrowsTripleRight } from "@/lib/icons";
const Pagination = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-auto flex w-full justify-center", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <nav role="navigation" aria-label="pagination" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
Pagination.displayName = "Pagination";
const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex list-none flex-row items-center gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <ul ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
PaginationContent.displayName = "PaginationContent";
const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <li ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
PaginationItem.displayName = "PaginationItem";
type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, "size"> & React.ComponentProps<typeof Link>;
const PaginationLink = t0 => {
  const $ = _c(13);
  let className;
  let isActive;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      isActive,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isActive;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    isActive = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "icon" : t1;
  const t2 = isActive ? "page" : undefined;
  let t3;
  if ($[5] !== className || $[6] !== isActive || $[7] !== size) {
    t3 = cn(buttonVariants({
      variant: isActive ? "outline" : "ghost",
      size
    }), className);
    $[5] = className;
    $[6] = isActive;
    $[7] = size;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t2 || $[11] !== t3) {
    t4 = <Link aria-current={t2} className={t3} {...props} />;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
PaginationLink.displayName = "PaginationLink";
type PaginationButtonProps = {
  isActive?: boolean;
} & Pick<ButtonProps, "size"> & React.ComponentProps<typeof Button>;
const PaginationButton = t0 => {
  const $ = _c(13);
  let className;
  let isActive;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      isActive,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isActive;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    isActive = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "icon" : t1;
  const t2 = isActive ? "page" : undefined;
  let t3;
  if ($[5] !== className || $[6] !== isActive || $[7] !== size) {
    t3 = cn(buttonVariants({
      variant: isActive ? "outline" : "ghost",
      size
    }), className);
    $[5] = className;
    $[6] = isActive;
    $[7] = size;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t2 || $[11] !== t3) {
    t4 = <Button aria-current={t2} className={t3} {...props} />;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
PaginationButton.displayName = "PaginationButton";
const PaginationPrevious = t0 => {
  const $ = _c(17);
  let buttonStyle;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      buttonStyle,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = buttonStyle;
    $[2] = className;
    $[3] = props;
  } else {
    buttonStyle = $[1];
    className = $[2];
    props = $[3];
  }
  const Component = buttonStyle === "button" ? PaginationButton : PaginationLink;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[4] !== t) {
    t1 = t("BUTTONS.BACKWARDS");
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const prevText = t1;
  let t2;
  if ($[6] !== className) {
    t2 = cn("gap-1 p-2 pl-2.5", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== prevText) {
    t3 = <span className="sr-only">{prevText}</span>;
    $[8] = prevText;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowsTripleLeft className="h-6" />;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== Component || $[12] !== prevText || $[13] !== props || $[14] !== t2 || $[15] !== t3) {
    t5 = <Component aria-label={prevText} size="default" className={t2} variant="ghost" {...props}>{t3}{t4}</Component>;
    $[11] = Component;
    $[12] = prevText;
    $[13] = props;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
PaginationPrevious.displayName = "PaginationPrevious";
const PaginationNext = t0 => {
  const $ = _c(17);
  let buttonStyle;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      buttonStyle,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = buttonStyle;
    $[2] = className;
    $[3] = props;
  } else {
    buttonStyle = $[1];
    className = $[2];
    props = $[3];
  }
  const Component = buttonStyle === "button" ? PaginationButton : PaginationLink;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[4] !== t) {
    t1 = t("BUTTONS.FORWARDS");
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const nextText = t1;
  let t2;
  if ($[6] !== className) {
    t2 = cn("gap-1 p-2 pr-2.5", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== nextText) {
    t3 = <span className="sr-only">{nextText}</span>;
    $[8] = nextText;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowsTripleRight className="h-6" />;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== Component || $[12] !== nextText || $[13] !== props || $[14] !== t2 || $[15] !== t3) {
    t5 = <Component aria-label={nextText} size="default" className={t2} variant="ghost" {...props}>{t3}{t4}</Component>;
    $[11] = Component;
    $[12] = nextText;
    $[13] = props;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
PaginationNext.displayName = "PaginationNext";
const PaginationEllipsis = t0 => {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex h-9 w-9 items-center justify-center", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DotsHorizontalIcon className="h-4 w-4" />;
    t3 = <span className="sr-only">...</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t1) {
    t4 = <span aria-hidden={true} className={t1} {...props}>{t2}{t3}</span>;
    $[7] = props;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
PaginationEllipsis.displayName = "PaginationEllipsis";
export { Pagination, PaginationContent, PaginationLink, PaginationButton, PaginationItem, PaginationPrevious, PaginationNext, PaginationEllipsis };